import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/cn.js';

/**
 * Flat fills, soft rounding, no keylines. `default` is the yellow one — a
 * screen should carry exactly one; everything else is `outline` or `ghost`.
 */
export const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill',
    'font-semibold tracking-tight transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
    'disabled:pointer-events-none disabled:opacity-45',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        default: 'bg-accent text-accent-text hover:bg-yellow-400 active:bg-yellow-600',
        secondary:
          'bg-surface-inverse text-text-inverse hover:bg-surface-inverse/85 active:bg-surface-inverse/75',
        outline:
          'border border-border-default bg-surface text-text hover:border-border-strong hover:bg-surface-hover',
        soft: 'bg-accent-soft text-accent-soft-text hover:bg-accent/25',
        ghost: 'text-text hover:bg-surface-hover',
        destructive:
          'bg-danger-solid text-danger-on hover:bg-danger-solid/90 focus-visible:ring-danger-solid',
        link: 'text-text underline decoration-accent decoration-2 underline-offset-4 hover:decoration-text',
      },
      size: {
        sm: 'h-8 px-3.5 text-xs',
        default: 'h-10 px-5 text-sm',
        lg: 'h-12 px-7 text-[15px]',
        icon: 'size-10',
        'icon-sm': 'size-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a <button>, keeping the styles. */
  asChild?: boolean;
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild = false, isLoading = false, disabled, children, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled ?? isLoading}
      {...props}
    >
      {/*
        Slot demands exactly one child, and the spinner slot counts even
        when it renders null — so `asChild` passes the child straight
        through. A link styled as a button has nothing to load anyway.
      */}
      {asChild ? (
        children
      ) : (
        <>
          {isLoading ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
          {children}
        </>
      )}
    </Comp>
  );
});
